import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Sparkles, X } from 'lucide-react';

type Message = {
  id: string;
  content: string;
  sender: 'user' | 'bot';
  timestamp: Date;
};

type ChatBotProps = {
  role?: string;
  onClose?: () => void;
  fullScreen?: boolean;
};

const ChatBot: React.FC<ChatBotProps> = ({ role = 'product-manager', onClose, fullScreen = false }) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content: `¡Hola! Soy tu asistente de Cortex AI para ${formatRole(role)}. Estoy aquí para acompañarte en tu viaje de aprendizaje. ¿En qué puedo ayudarte hoy?`,
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (chatRef.current && !chatRef.current.contains(event.target as Node)) {
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  function formatRole(role: string) {
    const roles: Record<string, string> = {
      'product-manager': 'Product Manager',
      'consultant': 'Consultoría',
      'developer': 'Desarrollo de Software',
      'project-manager': 'Project Management',
      'entrepreneur': 'Emprendimiento',
      'tech-entrepreneur': 'Tech Entrepreneurship',
      'digital-marketer': 'Marketing Digital'
    };
    return roles[role] || role;
  }

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: input,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    // Simulate bot response
    setTimeout(() => {
      const botMessage: Message = {
        id: Date.now().toString(),
        content: 'Lo siento, el servicio de chat está temporalmente deshabilitado.',
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMessage]);
      setIsTyping(false);
    }, 1000);
  };

  return (
    <div 
      ref={chatRef}
      className={`dark-card rounded-lg shadow-lg flex flex-col ${fullScreen ? 'fixed inset-0 z-50' : 'h-[500px]'}`}
    >
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 rounded-t-lg flex justify-between items-center">
        <div className="flex items-center">
          <Bot className="h-6 w-6 mr-2" />
          <h3 className="font-medium">Asistente de Cortex AI - {formatRole(role)}</h3>
        </div>
        {onClose && (
          <button 
            onClick={onClose} 
            className="text-white hover:text-white/80 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
      
      <div className="flex-1 p-4 overflow-y-auto bg-secondary/30">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`mb-4 flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg p-3 ${
                message.sender === 'user'
                  ? 'bg-accent text-white'
                  : 'bg-secondary border border-border text-foreground'
              }`}
            >
              <div className="flex items-start">
                {message.sender === 'bot' && (
                  <div className="flex-shrink-0 mr-2">
                    <Sparkles className="h-5 w-5 text-accent" />
                  </div>
                )}
                <div>
                  <p className="text-sm">{message.content}</p>
                  <p className="text-xs mt-1 opacity-70">
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                {message.sender === 'user' && (
                  <div className="flex-shrink-0 ml-2">
                    <User className="h-5 w-5 text-white" />
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="flex justify-start mb-4">
            <div className="bg-secondary border border-border rounded-lg p-3 text-foreground">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-accent/50 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-accent/50 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-accent/50 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      <form onSubmit={handleSendMessage} className="border-t border-border p-4 flex">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Escribe tu mensaje..."
          className="flex-1 border border-border rounded-l-lg px-4 py-2 bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
        />
        <button
          type="submit"
          className="bg-accent text-white px-4 py-2 rounded-r-lg hover:bg-accent/90 transition-colors"
        >
          <Send className="h-5 w-5" />
        </button>
      </form>
    </div>
  );
};

export default ChatBot;